/**
 * listino — gli strumenti che un'azienda paga con un canone mensile e una quota compresa
 * (atlante cap. 251, decisione di Simone del 06/10/2026).
 *
 * Il listino vive nei DATI, uno per strumento: `strumenti_a_canone/{capability}` (oggi `centralino_ai`
 * e `agente_whatsapp`). Il codice non conosce né i prezzi né gli strumenti: li legge da qui.
 *
 * Tre forme, dichiarate dai dati: canone + consumo (centralino, WhatsApp), SOLO consumo (misure SEO: canone 0,
 * unità `centesimi`), SOLO canone (`consumo: null`, 16/10/2026: un modulo che si paga a mese, come le Manutenzioni a
 * 99 €; Simone: «mi va bene 99 euro al mese»). Senza consumo non c'è contatore su Stripe né riga dell'extra in fattura. Lo scrive
 * lo script `scripts/strumenti/setup-stripe-strumenti.cjs` (prodotti, prezzi e misuratori su Stripe),
 * poi la console.
 *
 * Perché NON `app_addons`: ogni lettore di `app_addons` (comprese le copie di `requireCapability.ts`
 * nei codebase satellite) tratta ogni sua capability come «app per posto», da assegnare persona per
 * persona (`seat_apps`). Uno strumento a canone vale per tutta l'azienda.
 *
 * Nucleo PURO: niente Firebase, niente React. Lo usano il gestionale (vetrina, «Il mio abbonamento»)
 * e il server (attivazione, fatturazione, conteggio), copiato in `functions/src/_shared-pkg/strumenti`.
 */

import { suMisuraDallAzienda, versioniSuMisura } from './suMisura';

export const COLLEZIONE_STRUMENTI_A_CANONE = 'strumenti_a_canone';

/**
 * L'unità con cui si conta il consumo. Valori di enum: chiavi, mai tradotte.
 * `centesimi` = si conta direttamente l'IMPORTO, già al prezzo del cliente: è l'unità dei servizi che si pagano solo
 * a consumo, senza canone né quota, perché ogni uso costa una cifra diversa (misure SEO, analisi di Yuma: il costo
 * vero del fornitore per il ricarico, atlante cap. 251, tappa 5). Quota 0 e prezzo 0,01 € a unità.
 * `giri` = i calcoli del giro del tecnico delle Manutenzioni (Google Route Optimization, MANUTENZIONI-MODEL.md §20.3):
 * decisione di Simone del 18/10/2026, 100 compresi al mese per azienda, poi il costo vero per il ricarico.
 * `sblocchi` = i progettisti sbloccati nei Permessi edilizi (PERMESSI-EDILIZI-MODEL §12.1, schema approvato da Simone
 * il 29/10/2026): il canone del modulo ne comprende alcuni al mese, gli altri si pagano in fattura.
 */
export type UnitaDiConsumo = 'minuti' | 'messaggi' | 'centesimi' | 'giri' | 'sblocchi';
export const UNITA_DI_CONSUMO: readonly UnitaDiConsumo[] = ['minuti', 'messaggi', 'centesimi', 'giri', 'sblocchi'];

/** Stripe ha due mondi separati: le prove (`sk_test_`) e i soldi veri (`sk_live_`). */
export type AmbienteStripe = 'test' | 'live';

export interface ConsumoDelloStrumento {
  readonly unita: UnitaDiConsumo;
  /** Quante unità comprende il canone, ogni mese. */
  readonly quota_compresa: number;
  /** Il prezzo di ogni unità oltre la quota, in euro (0,40 = 40 centesimi). */
  readonly prezzo_extra_eur: number;
}

/**
 * Una versione di prezzo che non si vende più ma resta valida per chi ce l'ha nell'abbonamento (atlante cap. 251,
 * tappa 4). Un cambio di prezzo dalla console crea due prezzi NUOVI su Stripe; quelli di prima finiscono qui con le
 * condizioni che valevano, così il webhook continua a riconoscerli e ogni azienda vede quota, extra e tetto di ciò
 * che paga davvero (`strumentoPerLAzienda`).
 */
export interface VersionePrezzoPrecedente {
  readonly price_canone_id: string;
  /** Null per uno strumento SOLO a canone. */
  readonly price_consumo_id: string | null;
  readonly canone_mensile_eur: number;
  readonly consumo: ConsumoDelloStrumento | null;
  /** Fino a quando è stata in vendita (millisecondi); null se non si sa. */
  readonly fino_al_ms: number | null;
}

/** Gli oggetti Stripe di uno strumento, in un ambiente. */
export interface StripeDelloStrumento {
  readonly product_id: string;
  /** Prezzo fisso mensile (quantità 1): il canone. */
  readonly price_canone_id: string;
  /** Prezzo a consumo, legato al misuratore: prima fascia a 0 fino alla quota, poi l'extra. Null = solo canone. */
  readonly price_consumo_id: string | null;
  readonly meter_id: string | null;
  /** Il nome con cui si mandano a Stripe i consumi (`billing/meter_events`). Null = solo canone. */
  readonly meter_event_name: string | null;
  /** Le versioni di prezzo di prima, ancora valide per chi le ha. Vuoto finché il prezzo non cambia. */
  readonly prezzi_precedenti: readonly VersionePrezzoPrecedente[];
}

/**
 * Cosa fa lo strumento quando l'extra del mese arriva al tetto di spesa dell'azienda (`tetto.ts`):
 * `ferma_iniziativa` = smette di fare le cose di sua iniziativa (l'agente non scrive più per primo, ma risponde);
 * `solo_avviso` = continua, e si avvisa soltanto (il centralino risponde sempre);
 * `ferma_tutto` = non parte più niente che costi, finché non arriva il mese nuovo o il tetto sale (misure SEO,
 * analisi di Yuma: prima di partire si PRENOTA la stima sul tetto, `impegni.ts`). Valori di enum: mai tradotti.
 */
export type ComportamentoAlTetto = 'ferma_iniziativa' | 'solo_avviso' | 'ferma_tutto';
export const COMPORTAMENTI_AL_TETTO: readonly ComportamentoAlTetto[] = ['ferma_iniziativa', 'solo_avviso', 'ferma_tutto'];

export interface StrumentoACanone {
  /** Id del documento = la capability che lo strumento accende (`centralino_ai`). */
  readonly capability: string;
  /** Il nome in fattura e su Stripe: testo di un documento, non dell'interfaccia (non si traduce). */
  readonly etichetta: string;
  /** Falso = non si vende più a chi non ce l'ha; chi ce l'ha lo tiene. */
  readonly in_vendita: boolean;
  readonly canone_mensile_eur: number;
  /**
   * Cosa si conta oltre al canone. Null = strumento SOLO a canone (nessun contatore, nessun extra, nessun tetto di
   * spesa: si paga il canone e basta). Nel documento è `consumo: null` scritto apposta: un campo che manca resta un
   * documento malformato.
   */
  readonly consumo: ConsumoDelloStrumento | null;
  /** Al tetto di spesa: assente nel documento = `solo_avviso` (non si ferma niente senza averlo deciso). */
  readonly al_tetto: ComportamentoAlTetto;
  /**
   * Vero se lo strumento parla coi clienti presentandosi col nome dell'assistente dell'azienda («Sara»),
   * preso dalla configurazione dell'assistente. Serve a Yuma per capire che «Sara» è uno strumento e non una
   * collega (`functions/src/services/yuma/yumaAssistenti.ts`). Assente = falso.
   */
  readonly col_nome_dell_assistente: boolean;
  /**
   * Il tetto di spesa di partenza, in euro, finché l'azienda non ne sceglie uno (`tetto.ts`). Null = il canone
   * (centralino, WhatsApp: decisione del 07/10/2026); per i servizi senza canone serve un numero (20 € per misure SEO e
   * analisi di Yuma, decisione di Simone del 07/10/2026).
   */
  readonly tetto_predefinito_eur: number | null;
  /**
   * La funzione senza cui lo strumento non si attiva (`marketing_visibilita` per le misure SEO): chi non ce l'ha vede
   * l'invito a prenderla, non il bottone. Può stare in un piano o essere COMPRESA in un altro strumento (dal 28/10/2026 la
   * «Visibilità» è nel modulo «Gestione delle campagne»: `strumentoCheConcede`). Null = nessuna.
   */
  readonly richiede: string | null;
  /**
   * Vero se lo strumento funziona solo dopo una CONSEGNA della piattaforma (atlante cap. 264, decisione di Simone del
   * 10/10/2026: «il canone parte dal giorno in cui il numero risponde»). Il clic «Attiva» dell'amministratore scrive
   * allora una RICHIESTA (`consegna.ts`) e non tocca né la capability né l'abbonamento; lo accende la consegna, che passa
   * dalla via di sempre (`viaDiAttivazione`) e fa partire il canone da quel giorno. Assente = falso (si accende subito).
   */
  readonly richiede_consegna: boolean;
  /**
   * Vero se il tetto del mese (`al_tetto: 'ferma_tutto'`) FERMA il lavoro anche quando l'azienda non paga lo strumento
   * (`billing.exempt`, in prova): la fattura non c'è, ma la spesa per la piattaforma sì. Serve ai consumi che fa partire
   * CHIUNQUE dall'esterno (le simulazioni AI chieste da chi scrive al numero WhatsApp, atlante cap. 266): senza, un
   * estraneo potrebbe far spendere senza limite a un'azienda gratuita. La fatturazione non cambia: chi non paga non
   * riceve addebiti. Assente = falso (per chi non paga nessun tetto, come prima). Lo legge `impegni.ts`
   * (`tettoFermaIlLavoro`), in modo generico.
   */
  readonly tetto_anche_senza_fattura?: boolean;
  /**
   * Le ALTRE funzioni che lo strumento accende insieme alla sua (28/10/2026, decisione di Simone: il modulo «Gestione
   * delle campagne» comprende tutta «Campagne e risultati», che esce dal piano Pro). Si accendono e si spengono con lo
   * strumento, per qualunque via (vetrina, Yuma, console, abbonamento): `funzioniDelloStrumento`. Assente = nessuna.
   */
  readonly comprende?: readonly string[];
  /**
   * I mesi di impegno minimo dal giorno dell'attivazione (28/10/2026, decisione di Simone: 12 per «Gestione delle
   * campagne», «almeno 12 mesi ce li paghino»). Spegnerlo prima vuol dire spegnerlo alla fine dell'impegno, e si attiva
   * solo dopo l'approvazione specifica della clausola (`clausolaImpegno.ts`). Assente o null = nessun impegno.
   */
  readonly impegno_minimo_mesi?: number | null;
  /**
   * Falso se lo strumento NON si attiva durante la prova gratuita, né senza carta né con la carta (28/10/2026, decisione
   * di Simone: altrimenti si attiva gratis, si usa e si spegne prima di pagare). Si attiva quando l'abbonamento è a
   * pagamento. Assente = vero (si attiva gratis fino alla fine della prova, come gli altri).
   */
  readonly attivabile_in_prova?: boolean;
  /**
   * SU COSA si compra (atlante cap. 448, tappa 2; decisione di Simone del 29/10/2026: «Gestione delle campagne» si
   * compra per marchio, «sempre uguale» per ogni marchio). `azienda`: un canone per l'azienda, come tutti fino a oggi.
   * `marchio`: un canone per ogni marchio attivato (l'azienda stessa è un marchio, `principale`), sulla STESSA riga
   * Stripe con la quantità = marchi attivati, ognuno col suo impegno. Assente = `azienda`: nessuno strumento cambia.
   */
  readonly unita?: UnitaDelloStrumento;
  readonly stripe: Partial<Record<AmbienteStripe, StripeDelloStrumento>>;
}

/** Su cosa si compra uno strumento: l'azienda intera, o un marchio alla volta. */
export const UNITA_DEGLI_STRUMENTI = ['azienda', 'marchio'] as const;
export type UnitaDelloStrumento = (typeof UNITA_DEGLI_STRUMENTI)[number];

/** Su cosa si compra: assente o sconosciuto = l'azienda (il comportamento di sempre). */
export function unitaDelloStrumento(s: Pick<StrumentoACanone, 'unita'>): UnitaDelloStrumento {
  return s.unita === 'marchio' ? 'marchio' : 'azienda';
}

/** Tutte le funzioni che lo strumento accende: la sua e quelle che comprende, senza doppioni. */
export function funzioniDelloStrumento(s: Pick<StrumentoACanone, 'capability' | 'comprende'>): readonly string[] {
  return [...new Set([s.capability, ...(s.comprende ?? [])])];
}

/** I mesi di impegno minimo, o null se lo strumento non ne ha. */
export function impegnoMinimoMesi(s: Pick<StrumentoACanone, 'impegno_minimo_mesi'>): number | null {
  const m = s.impegno_minimo_mesi;
  return typeof m === 'number' && Number.isInteger(m) && m > 0 ? m : null;
}

/** Vero se lo strumento si attiva anche durante la prova gratuita (il caso di tutti, salvo dichiarazione contraria). */
export function attivabileInProva(s: Pick<StrumentoACanone, 'attivabile_in_prova'>): boolean {
  return s.attivabile_in_prova !== false;
}

/**
 * Lo strumento che CONCEDE una funzione: quello che la accende come sua o fra quelle che comprende. Serve a dire «si
 * ottiene col modulo X» invece di «cambia piano» quando una funzione non sta più in nessun piano. Null = nessuno.
 */
export function strumentoCheConcede(capability: string, listino: readonly StrumentoACanone[]): StrumentoACanone | null {
  return listino.find((s) => funzioniDelloStrumento(s).includes(capability)) ?? null;
}

/**
 * Le aggiunte (`modules.addon`) dopo lo spegnimento di uno strumento: via la sua funzione e quelle che comprende, MA
 * non quelle che un altro strumento ancora acceso concede a sua volta. Funzione pura: la usano tutte le vie che
 * spengono (vetrina, Yuma, console, abbonamento).
 */
export function aggiunteDaTogliere(
  s: Pick<StrumentoACanone, 'capability' | 'comprende'>,
  aggiunteOra: readonly string[],
  listino: readonly StrumentoACanone[],
): readonly string[] {
  const altriAccesi = listino.filter((x) => x.capability !== s.capability && aggiunteOra.includes(x.capability));
  const tenute = new Set(altriAccesi.flatMap((x) => funzioniDelloStrumento(x)));
  return funzioniDelloStrumento(s).filter((c) => aggiunteOra.includes(c) && !tenute.has(c));
}

/**
 * Lo strumento si paga SOLO a consumo: niente canone, niente quota, si conta l'importo (`centesimi`). Il gestionale lo
 * racconta come «paghi solo quello che usi» invece che con canone e quota.
 */
export function soloAConsumo(s: Pick<StrumentoACanone, 'canone_mensile_eur' | 'consumo'>): boolean {
  return s.consumo !== null && s.consumo.unita === 'centesimi' && s.canone_mensile_eur === 0 && s.consumo.quota_compresa === 0;
}

/** Lo strumento si paga SOLO col canone: niente consumo da contare, niente extra né tetto (`consumo: null`). */
export function soloCanone(s: Pick<StrumentoACanone, 'consumo'>): boolean {
  return s.consumo === null;
}

/** Uno strumento che conta un consumo (canone + consumo, o solo consumo): l'unico che ha quota, extra e tetto. */
export type StrumentoConConsumo = StrumentoACanone & { readonly consumo: ConsumoDelloStrumento };

export function conConsumo(s: StrumentoACanone): s is StrumentoConConsumo {
  return s.consumo !== null;
}

/**
 * Il consumo di uno strumento in un punto che conta (registrare un consumo, prenotare una stima, il tetto): uno strumento
 * solo a canone lì non deve arrivare, e se arriva è un errore di chi chiama, non un consumo da inventare.
 */
export function consumoObbligatorio(s: StrumentoACanone): ConsumoDelloStrumento {
  if (s.consumo === null) throw new Error(`[strumenti] «${s.capability}» si paga solo col canone: non ha consumi da contare`);
  return s.consumo;
}

export type EsitoLettura =
  | { readonly ok: true; readonly strumento: StrumentoACanone }
  | { readonly ok: false; readonly motivo: string };

const ID_VALIDO = /^[a-z0-9_]{1,64}$/;

function numeroPositivoOZero(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0;
}

function testoPieno(v: unknown): v is string {
  return typeof v === 'string' && v.trim() !== '';
}

/** Il consumo di un documento (listino o versione precedente), o null se non è valido. */
function consumoDaDati(v: unknown): ConsumoDelloStrumento | null {
  const c = (typeof v === 'object' && v !== null ? v : {}) as Record<string, unknown>;
  if (!UNITA_DI_CONSUMO.includes(c.unita as UnitaDiConsumo)) return null;
  if (!numeroPositivoOZero(c.quota_compresa) || !Number.isInteger(c.quota_compresa)) return null;
  if (!numeroPositivoOZero(c.prezzo_extra_eur)) return null;
  return { unita: c.unita as UnitaDiConsumo, quota_compresa: c.quota_compresa, prezzo_extra_eur: c.prezzo_extra_eur };
}

/** Un istante che arriva da Firestore (Timestamp), come numero o come `{ seconds }`. Null se non si legge. */
function millisecondi(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'object' || v === null) return null;
  const o = v as { toMillis?: unknown; seconds?: unknown; _seconds?: unknown };
  if (typeof o.toMillis === 'function') return (o.toMillis as () => number)();
  const s = typeof o.seconds === 'number' ? o.seconds : typeof o._seconds === 'number' ? o._seconds : null;
  return s === null ? null : s * 1000;
}

function versioneDaDati(v: unknown): VersionePrezzoPrecedente | null {
  if (typeof v !== 'object' || v === null) return null;
  const d = v as Record<string, unknown>;
  // Una versione SOLO a canone: consumo e prezzo del consumo nulli tutti e due, mai uno solo.
  const senzaConsumo = d.consumo === null && (d.price_consumo_id === null || d.price_consumo_id === undefined);
  const consumo = senzaConsumo ? null : consumoDaDati(d.consumo);
  if (!testoPieno(d.price_canone_id) || !numeroPositivoOZero(d.canone_mensile_eur)) return null;
  if (!senzaConsumo && (!testoPieno(d.price_consumo_id) || !consumo)) return null;
  return {
    price_canone_id: d.price_canone_id,
    price_consumo_id: senzaConsumo ? null : (d.price_consumo_id as string),
    canone_mensile_eur: d.canone_mensile_eur,
    consumo,
    fino_al_ms: millisecondi(d.fino_al),
  };
}

/** Gli oggetti Stripe di un ambiente; quelli del consumo (prezzo e misuratore) servono solo se lo strumento ne ha uno. */
function stripeDaDati(v: unknown, conConsumo: boolean): StripeDelloStrumento | null {
  if (typeof v !== 'object' || v === null) return null;
  const d = v as Record<string, unknown>;
  const campi = conConsumo ? (['product_id', 'price_canone_id', 'price_consumo_id', 'meter_id', 'meter_event_name'] as const) : (['product_id', 'price_canone_id'] as const);
  if (!campi.every((c) => testoPieno(d[c]))) return null;
  const precedenti = Array.isArray(d.prezzi_precedenti) ? d.prezzi_precedenti : [];
  return {
    product_id: d.product_id as string,
    price_canone_id: d.price_canone_id as string,
    price_consumo_id: conConsumo ? (d.price_consumo_id as string) : null,
    meter_id: conConsumo ? (d.meter_id as string) : null,
    meter_event_name: conConsumo ? (d.meter_event_name as string) : null,
    // Una versione illeggibile si scarta: il suo prezzo non si riconoscerebbe, ma le altre sì.
    prezzi_precedenti: precedenti.map(versioneDaDati).filter((x): x is VersionePrezzoPrecedente => x !== null),
  };
}

/**
 * Legge un documento del listino. Un documento malformato NON diventa uno strumento con valori
 * inventati: si scarta con il motivo, e chi legge lo scrive nel registro.
 * Gli oggetti Stripe incompleti di un ambiente si scartano (lo strumento esiste, ma in quell'ambiente
 * non si può ancora vendere).
 */
export function strumentoDalDocumento(id: string, dati: unknown): EsitoLettura {
  if (!ID_VALIDO.test(id)) return { ok: false, motivo: `id «${id}» non valido (serve snake_case ASCII)` };
  if (typeof dati !== 'object' || dati === null) return { ok: false, motivo: `«${id}»: documento vuoto` };
  const d = dati as Record<string, unknown>;
  if (!testoPieno(d.etichetta)) return { ok: false, motivo: `«${id}»: manca l'etichetta` };
  if (!numeroPositivoOZero(d.canone_mensile_eur)) return { ok: false, motivo: `«${id}»: canone non valido` };
  // Solo canone: `consumo: null` scritto apposta, e allora serve un canone vero (uno strumento gratis non si vende).
  let consumo: ConsumoDelloStrumento | null = null;
  if (d.consumo === null) {
    if (d.canone_mensile_eur <= 0) return { ok: false, motivo: `«${id}»: uno strumento senza consumo ha bisogno di un canone` };
  } else {
    const c = (typeof d.consumo === 'object' && d.consumo !== null ? d.consumo : {}) as Record<string, unknown>;
    if (!UNITA_DI_CONSUMO.includes(c.unita as UnitaDiConsumo)) {
      return { ok: false, motivo: `«${id}»: unità di consumo non valida` };
    }
    if (!numeroPositivoOZero(c.quota_compresa) || !Number.isInteger(c.quota_compresa)) {
      return { ok: false, motivo: `«${id}»: quota compresa non valida (serve un intero ≥ 0)` };
    }
    if (!numeroPositivoOZero(c.prezzo_extra_eur)) return { ok: false, motivo: `«${id}»: prezzo dell'extra non valido` };
    consumo = consumoDaDati(c) as ConsumoDelloStrumento;
  }
  const s = (typeof d.stripe === 'object' && d.stripe !== null ? d.stripe : {}) as Record<string, unknown>;
  const stripe: Partial<Record<AmbienteStripe, StripeDelloStrumento>> = {};
  for (const amb of ['test', 'live'] as const) {
    const letto = stripeDaDati(s[amb], consumo !== null);
    if (letto) stripe[amb] = letto;
  }
  return {
    ok: true,
    strumento: {
      capability: id,
      etichetta: (d.etichetta as string).trim(),
      in_vendita: d.in_vendita !== false,
      canone_mensile_eur: d.canone_mensile_eur as number,
      consumo,
      al_tetto: COMPORTAMENTI_AL_TETTO.includes(d.al_tetto as ComportamentoAlTetto) ? (d.al_tetto as ComportamentoAlTetto) : 'solo_avviso',
      col_nome_dell_assistente: d.col_nome_dell_assistente === true,
      tetto_predefinito_eur: numeroPositivoOZero(d.tetto_predefinito_eur) ? d.tetto_predefinito_eur : null,
      richiede: typeof d.richiede === 'string' && ID_VALIDO.test(d.richiede) ? d.richiede : null,
      richiede_consegna: d.richiede_consegna === true,
      tetto_anche_senza_fattura: d.tetto_anche_senza_fattura === true,
      // Un id malformato in `comprende` si scarta: accenderebbe una funzione che non esiste.
      comprende: Array.isArray(d.comprende) ? d.comprende.filter((c): c is string => typeof c === 'string' && ID_VALIDO.test(c) && c !== id) : [],
      impegno_minimo_mesi: typeof d.impegno_minimo_mesi === 'number' && Number.isInteger(d.impegno_minimo_mesi) && d.impegno_minimo_mesi > 0 ? d.impegno_minimo_mesi : null,
      attivabile_in_prova: d.attivabile_in_prova !== false,
      unita: d.unita === 'marchio' ? 'marchio' : 'azienda',
      stripe,
    },
  };
}

/** L'ambiente di una chiave Stripe, dal suo prefisso. Una chiave sconosciuta non si indovina. */
export function ambienteDellaChiave(chiave: string): AmbienteStripe | null {
  if (/^(sk|rk)_test_/.test(chiave)) return 'test';
  if (/^(sk|rk)_live_/.test(chiave)) return 'live';
  return null;
}

/**
 * Che ruolo ha un prezzo Stripe in uno strumento. Ogni nostro prezzo lo dichiara su di sé
 * (`metadata.yumaos_strumento`, `metadata.ruolo`): le righe di un abbonamento si riconoscono da lì
 * (`functions/src/services/strumenti/righeAbbonamento.ts`), per qualunque versione e anche su misura.
 */
export interface PrezzoDiStrumento {
  readonly capability: string;
  readonly ruolo: 'canone' | 'consumo';
}


/**
 * Lo strumento con le condizioni che QUESTA azienda paga, o pagherà (atlante cap. 251, tappa 4):
 *  - se nel suo abbonamento ha delle righe dello strumento (`billing.strumenti.<cap>.price_canone_id` /
 *    `price_consumo_id`), valgono le condizioni di QUEI prezzi: una versione precedente del listino, o una versione su
 *    misura (attuale o di prima, `suMisura.ts`). Canone e consumo si guardano separatamente, perché riaccendendo uno
 *    strumento la riga del consumo di prima può restare accanto a un canone nuovo;
 *  - se non ce le ha (non l'ha ancora attivato, o non ha l'abbonamento), valgono le condizioni su misura se ne ha,
 *    altrimenti quelle di oggi del listino.
 * Idempotente: applicarla due volte non cambia niente.
 *
 * È l'unica porta per i calcoli di un'azienda: quota, extra, avvisi, tetto predefinito, «Il mio abbonamento», console.
 */
export function strumentoPerLAzienda(datiAzienda: unknown, s: StrumentoACanone): StrumentoACanone {
  const billing = (datiAzienda as { billing?: { strumenti?: Record<string, unknown> } } | null | undefined)?.billing;
  const stato = billing?.strumenti?.[s.capability] as { price_canone_id?: unknown; price_consumo_id?: unknown } | undefined;
  const suMisura = suMisuraDallAzienda(datiAzienda, s);
  const paga = (v: unknown): v is string => typeof v === 'string' && v !== '';
  if (!stato || (!paga(stato.price_canone_id) && !paga(stato.price_consumo_id))) {
    const a = suMisura.attuale;
    return a ? { ...s, canone_mensile_eur: a.canone_mensile_eur, consumo: a.consumo } : s;
  }
  const versioni: ReadonlyArray<{ price_canone_id: string; price_consumo_id: string | null; canone_mensile_eur: number; consumo: ConsumoDelloStrumento | null }> = [
    ...(['test', 'live'] as const).flatMap((amb) => s.stripe[amb]?.prezzi_precedenti ?? []),
    ...versioniSuMisura(datiAzienda, s),
  ];
  const vCanone = paga(stato.price_canone_id) ? versioni.find((v) => v.price_canone_id === stato.price_canone_id) : undefined;
  // Un prezzo del consumo che manca (strumento solo a canone) non «corrisponde» a un'altra versione senza consumo.
  const vConsumo = paga(stato.price_consumo_id) ? versioni.find((v) => v.price_consumo_id === stato.price_consumo_id) : undefined;
  if (!vCanone && !vConsumo) return s;
  return {
    ...s,
    canone_mensile_eur: vCanone ? vCanone.canone_mensile_eur : s.canone_mensile_eur,
    consumo: vConsumo ? vConsumo.consumo : s.consumo,
  };
}

/**
 * Quanto costa l'extra di un consumo, in centesimi: le unità oltre la quota per il prezzo.
 * È la stessa aritmetica che fa Stripe con i due scaglioni, e serve a MOSTRARE la stima
 * nel gestionale prima che arrivi la fattura.
 */
export function extraInCentesimi(consumo: ConsumoDelloStrumento, unitaConsumate: number): number {
  const oltre = Math.max(0, Math.floor(unitaConsumate) - consumo.quota_compresa);
  return Math.round(oltre * consumo.prezzo_extra_eur * 100);
}
